import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { push } from 'components/Navigation/navigationRef';
import { Screens, Stacks } from 'components/Navigation/types';
import { useSelector } from 'hooks/useSelector';
import { pluralise } from 'lib/pluralise';
import { isEqual } from 'lodash';
import React from 'react';

import { CountChips } from 'screens/AssessmentScreen/CountChips';
import {
  selectAseTplFieldToGroup,
  selectAseTplFieldGroup,
  selectAseFieldGroupStats,
  selectAseFieldInstancesCountForGroup,
  selectAseIsEditable,
} from 'store/assessments/selectors';

type Props = {
  // The instance ID that holds the instances (e.g. Flat)
  parentInstanceId: string | null;
  // The field ID of the parent instance that holds the results
  parentFieldId: string;
  // The group that is added to the parent (e.g. Flat Door)
  childGroupId: string;
  isModal?: boolean;
};

export const ChildGroupAssessment: React.FC<Props> = ({
  parentFieldId,
  parentInstanceId,
  childGroupId,
  isModal,
}) => {
  const isEditable = useSelector(selectAseIsEditable);
  const childGroup = useSelector((state) =>
    selectAseTplFieldGroup(state, childGroupId),
  );

  const parentGroup = useSelector((state) =>
    selectAseTplFieldToGroup(state, parentFieldId),
  );

  // Get total number of instances in asset and in assessment
  const totalInstanceCount = useSelector((state) =>
    selectAseFieldInstancesCountForGroup(state, {
      groupId: childGroupId,
      parent: null,
      inAssessmentOnly: false,
    }),
  );

  const groupStats = useSelector(
    (state) =>
      selectAseFieldGroupStats(state, {
        groupId: childGroupId,
        parent: {
          fieldId: parentFieldId,
          instanceId: parentInstanceId,
        },
        inAssessmentOnly: true,
      }),
    isEqual,
  );

  if (!childGroup || !parentGroup) {
    return null;
  }

  const unaddedLinkedCount = isEditable
    ? groupStats.childInstancesCount - groupStats.assessmentInstancesCount
    : 0;

  const unaddedOtherCount = isEditable
    ? totalInstanceCount - groupStats.childInstancesCount
    : 0;

  return (
    <Card
      label={childGroup.name}
      explanation={
        !isEditable
          ? undefined
          : groupStats.assessmentInstancesCount
            ? pluralise(
                unaddedOtherCount,
                '1 other available',
                '{x} others available',
                '',
              )
            : pluralise(unaddedOtherCount, '1 available', '{x} available', '')
      }
    >
      <CardRow
        label={
          unaddedLinkedCount
            ? `${pluralise(
                groupStats.assessmentInstancesCount,
                '1',
                '{x}',
              )}/${pluralise(
                groupStats.childInstancesCount,
                `1 ${childGroup.calculatedInfo.singularName}`,
                `{x} ${childGroup.name}`,
              )}`
            : pluralise(
                groupStats.assessmentInstancesCount,
                `1 ${childGroup.calculatedInfo.singularName}`,
                `{x} ${childGroup.name}`,
              )
        }
        onPress={
          isModal
            ? undefined
            : () =>
                push(Stacks.ROOT, Screens.ROOT_ASEREPEATABLE, {
                  groupId: childGroupId,
                  parent: {
                    fieldId: parentFieldId,
                    instanceId: parentInstanceId,
                  },
                })
        }
        navigationType={isModal ? undefined : 'screen'}
      >
        <CountChips
          questions={groupStats.questionsCount}
          questionsCompleted={groupStats.completedQuestionsCount}
          checklists={groupStats.checklistsCount}
          checklistsCompleted={groupStats.completedChecklistsCount}
          actions={groupStats.actionsCount}
          controls={groupStats.controlsCount}
          unreviewedActions={0}
          unreviewedControls={0}
        />
      </CardRow>
    </Card>
  );
};
